// src/components/Login.js
import React, { useState } from 'react';
import {
  Container, TextField, Button, Typography, Card, CardContent,
  InputAdornment, IconButton, Box, Snackbar, Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login } from '../services/authService';
import { useNavigate } from 'react-router-dom';

function Login({ onLogin }) {
  const [formData, setFormData] = useState({ correo: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  const validarCorreo = (email) => /^\S+@\S+\.\S+$/.test(email);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validarCorreo(formData.correo)) {
      setError('Formato de correo inválido');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Debug extra: confirmar payload completo
      console.log('[DEBUG FRONTEND] Payload enviado:', formData);

      // Llamada al servicio de login
      const resLogin = await login(formData.correo.trim().toLowerCase(), formData.password);

      if (!resLogin || resLogin.success === false) {
        setError(resLogin?.mensaje || 'Error en login');
        setFormData({ ...formData, password: '' });
        return;
      }

      if (!resLogin.token) {
        setError('El servidor no devolvió un token válido');
        setFormData({ ...formData, password: '' });
        return;
      }

      // Normalizar datos
      const rolNormalizado = (resLogin.rol || resLogin.usuario?.rol || '').toLowerCase().trim();
      const correoUsuario = resLogin.usuario?.correo || resLogin.correo || formData.correo.trim().toLowerCase();
      const casilleroCodigo = resLogin.usuario?.casilleroCodigo || resLogin.casillero || null;

      // Guardar en localStorage
      localStorage.setItem('token', resLogin.token);
      if (rolNormalizado) localStorage.setItem('rol', rolNormalizado);
      if (correoUsuario) localStorage.setItem('correo', correoUsuario);
      if (casilleroCodigo) localStorage.setItem('casilleroCodigo', casilleroCodigo);

      if (onLogin) {
        onLogin(rolNormalizado, correoUsuario, casilleroCodigo);
      }

      const userName = resLogin.usuario?.nombre || correoUsuario;
      setWelcomeMsg(`🎉 Bienvenido, ${userName}!`);
      setOpen(true);

      // Redirección según rol
      if (rolNormalizado === 'cliente') {
        navigate('/consulta');
      } else if (rolNormalizado === 'admin' || rolNormalizado === 'superadmin') {
        navigate('/dashboard');
      } else {
        navigate('/');
      }

      setFormData({ correo: '', password: '' });
    } catch (err) {
      console.error('[DEBUG FRONTEND] ❌ Error capturado:', err.message || err);
      setError(err?.response?.data?.mensaje || 'Error al iniciar sesión');
      setFormData({ ...formData, password: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card sx={{ boxShadow: 4, borderRadius: 3 }}>
        <form onSubmit={handleLogin}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              🔑 Iniciar sesión
            </Typography>

            <TextField
              id="correo"
              label="Correo"
              fullWidth
              margin="normal"
              value={formData.correo}
              onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
              autoComplete="username"
              error={!!formData.correo && !validarCorreo(formData.correo)}
              helperText={formData.correo && !validarCorreo(formData.correo) ? 'Correo inválido' : ''}
            />

            <TextField
              id="password"
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!formData.correo || !formData.password || loading}
              >
                {loading ? 'Ingresando…' : 'Ingresar'}
              </Button>
              <Button
                type="reset"
                variant="text"
                color="secondary"
                onClick={() => setFormData({ correo: '', password: '' })}
                disabled={loading}
              >
                Limpiar
              </Button>
            </Box>
          </CardContent>
        </form>
      </Card>

      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: '100%' }}>
          {welcomeMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Login;